import { Grid, type GridProps } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import isNil from "lodash-es/isNil";
import { observer } from "mobx-react-lite";
import { logger } from "../../../common/logger";
import type { EmbeddedEntryContextType } from "../../../common/types/cmsTypes";
import type { Product, ProductTileCallbacks } from "../../../common/types/productTypes";
import type { GAListInformation, LIST_TYPE } from "../../../common/types/trackingTypes";
import productStore from "../../../stores/product/productStore";
import Button from "../../Button";
import ProductTile from "./ProductTile";

type ProductListProps = {
   heading?: string | null;
   subtitle?: string | null;
   products?: Product[];
   skus?: string[];
   count?: number;
   buttonText?: string;
   buttonUrl?: string;
   listName: string;
   listType: LIST_TYPE;
   context: EmbeddedEntryContextType;
   callbacks?: ProductTileCallbacks;
};

const ProductList = ({
   heading,
   subtitle,
   products,
   skus = [],
   count,
   buttonText,
   buttonUrl,
   listName,
   listType,
   context,
   callbacks = {}
}: ProductListProps) => {
   const resolvedProducts: Product[] = !isNil(products) ? products : productStore.resolveSkus(skus);
   if (resolvedProducts.length === 0) {
      logger.log("products", "No products resolved: ", skus, resolvedProducts);
      return null;
   }

   const visibleSlice = !isNil(count) ? resolvedProducts.slice(0, count) : resolvedProducts;
   const hasSubtitle = !isNil(subtitle);

   const listInformation: GAListInformation = { type: listType, name: listName, length: visibleSlice.length };

   const standardArticleTileStyle = cn(
      "h-auto",
      "basis-full min-[240px]:basis-[156px] min-[375px]:basis-[171px] min-[424px]:basis-[49%] min-[585px]:basis-[32%] md:basis-[24%] lg:basis-[19.2%] xl:basis-[16%]",
      "min-[240px]:max-w-40 min-[375px]:max-w-44 min-[424px]:max-w-[49%] min-[585px]:max-w-[32%] md:max-w-[24%] lg:max-w-[19.2%] xl:max-w-[16%]"
   );
   if (context === "standard-article") {
      return (
         <div className="w-full">
            <div className="mx-auto w-3/4 max-w-[848px] text-center">
               <h2 className="mb-4">{heading}</h2>
               {hasSubtitle && <p className="mb-4">{subtitle}</p>}

               {buttonUrl && buttonText && (
                  <Button to={buttonUrl} variant="secondary">
                     {buttonText}
                  </Button>
               )}
            </div>
            <div className="flex flex-shrink grow flex-row flex-wrap justify-center gap-2">
               {visibleSlice.map((product, i) => (
                  <ProductTile
                     key={product.sku}
                     product={product}
                     callbacks={callbacks}
                     listInformation={listInformation}
                     className={standardArticleTileStyle}
                     position={i + 1}
                  />
               ))}
            </div>
         </div>
      );
   }

   const gridProps: Omit<GridProps, "children"> = {
      type: "container",
      cols: {
         base: 1,
         "300px": 2,
         "530px": 3,
         "750px": 4,
         "950px": 5,
         "1180px": 6
      },
      gap: {
         base: 1,
         "300px": 2
      }
   };

   const productGrid = (
      <Grid {...gridProps}>
         {visibleSlice.map((product, i) => (
            <ProductTile
               key={product.sku}
               product={product}
               callbacks={callbacks}
               listInformation={listInformation}
               position={i + 1}
            />
         ))}
      </Grid>
   );

   if (context === "category" || context === "search") {
      return productGrid;
   }

   return (
      <div className="w-full">
         {!isNil(heading) && (
            <div className="max-w-[680px]">
               <h2>{heading}</h2>
               {hasSubtitle && <p>{subtitle}</p>}
               {buttonUrl && buttonText && (
                  <Button to={buttonUrl} variant="secondary">
                     {buttonText}
                  </Button>
               )}
            </div>
         )}
         {productGrid}
      </div>
   );
};

export default observer(ProductList);
